import axios, { AxiosError, CanceledError, InternalAxiosRequestConfig } from 'axios';

import { redirectForSSOLogin, REFRESH_TOKEN_STORAGE_KEY, SESSION_STORAGE_KEY } from '../auth/auth-provider-utils';
import { getSession, logout } from '../auth/session';

export type TLeaderBoardUser = {
  id: string;
  name: string;
  email: string;
  role: string;
};

export type TSetSession = {
  token: string;
  user: {
    email: string;
    name: string;
  };
};

export const axiosAbortController = new AbortController();

// TODO: add current workspace
// const currentWorksapce = localStorage.getItem('WORKSPACE_CONTEXT') as TAppNameEnum;

// const newBaseURL = APIBaseURLs[currentWorksapce ?? EAppNameEnum.FIT];

// baseURL starts blank; each FE app sets axiosServiceInstance.defaults.baseURL
// during initialization (single-BFF apps to their HOST_API, dashboard per workspace).
const axiosInstance = axios.create({
  baseURL: '',
  signal: axiosAbortController.signal,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if we're currently refreshing to avoid multiple refresh calls
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

// Observable refresh state for UI preloader
type RefreshStateListener = (refreshing: boolean) => void;
const refreshStateListeners = new Set<RefreshStateListener>();

const setRefreshing = (value: boolean) => {
  isRefreshing = value;
  refreshStateListeners.forEach(listener => listener(value));
};

export const subscribeRefreshState = (listener: RefreshStateListener): (() => void) => {
  refreshStateListeners.add(listener);
  return () => refreshStateListeners.delete(listener);
};

export const getIsRefreshing = () => isRefreshing;

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

// Raw axios instance for token refresh (not intercepted).
// baseURL is supplied per-request from axiosInstance.defaults.baseURL so it
// follows whatever endpoint the app configured at init.
const refreshAxios = axios.create({
  baseURL: '',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Paths that bypass the Supabase session check (no auth required).
// Includes auth endpoints (login, reset, etc.) and public API endpoints.
const publicAuthPaths = [
  '/auth/send-password-reset',
  '/auth/set-password',
  '/auth/login',
  '/auth/verify',
  '/auth/validate-sso',
  '/auth/consume-ott',
  '/auth/refresh-token',
  '/auth/request-magic-link',
  '/auth/sign-out',
  '/auth/supabase-login',
  '/auth/unified-login',
  '/auth/validate-reset-token',
  '/auth/login-dashboard',
  // Token-based email-change links — public on the backend and opened from an
  // email (often without an active session), so they must skip the session
  // check and the 401 refresh/redirect machinery.
  'email-change/confirm',
  'email-change/cancel',
  // Public API endpoints (no auth guard on backend)
  'contents/public',
  'instructors/slug/',
];

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Block requests without a valid session.
    // This prevents 401 cascades from ungated queries (e.g. useGetJournalsQuery)
    // that would trigger redirect loops via the response interceptor.
    const isPublicEndpoint = publicAuthPaths.some(path => config.url?.endsWith(path) || config.url?.includes(path));
    if (!isPublicEndpoint && !config.headers.Authorization && !getSession()) {
      const error = new CanceledError('No active Supabase session');
      throw error;
    }

    return config;
  },
  error => {
    return Promise.reject((error.response && error.response.data) || 'something went wrong');
  },
);

// Response interceptor for handling token refresh
axiosInstance.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Only handle 401 errors for Supabase auth
    if (error.response?.status !== 401 || originalRequest._retry) {
      throw error;
    }

    // Skip token refresh for public auth endpoints (e.g., validate-sso during SSO flow)
    const isPublicAuthEndpoint = publicAuthPaths.some(path => originalRequest.url?.endsWith(path));
    if (isPublicAuthEndpoint) {
      throw error;
    }

    // Mark this request as retried to avoid infinite loops
    originalRequest._retry = true;

    // If already refreshing, wait for the new token
    if (isRefreshing) {
      return new Promise(resolve => {
        subscribeTokenRefresh((token: string) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(axiosInstance(originalRequest));
        });
      });
    }

    setRefreshing(true);

    try {
      // eslint-disable-next-line no-console
      console.log('%c[Axios]%c Token expired, refreshing...', 'color: #F59E0B; font-weight: bold', 'color: inherit');

      const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

      if (!refreshToken) {
        console.warn(
          '%c[Axios]%c No refresh token available, clearing session',
          'color: #EF4444; font-weight: bold',
          'color: inherit',
        );
        await logout();
        removeTokenFromAxios();
        setRefreshing(false);
        redirectForSSOLogin();
        throw error;
      }

      const { data } = await refreshAxios.post(
        '/auth/refresh-token',
        { refresh_token: refreshToken },
        { baseURL: axiosInstance.defaults.baseURL },
      );

      const newToken = data.access_token;
      const newRefreshToken = data.refresh_token;

      if (!newToken) {
        console.warn(
          '%c[Axios]%c Failed to refresh token, clearing session',
          'color: #EF4444; font-weight: bold',
          'color: inherit',
        );
        await logout();
        removeTokenFromAxios();
        setRefreshing(false);
        redirectForSSOLogin();
        throw error;
      }

      // eslint-disable-next-line no-console
      console.log('%c[Axios]%c Token refreshed successfully', 'color: #3ECF8E; font-weight: bold', 'color: inherit');

      // Update the default authorization header
      setTokenToAxios(newToken);

      // Update localStorage session
      localStorage.setItem(SESSION_STORAGE_KEY, newToken);
      if (newRefreshToken) {
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, newRefreshToken);
      }

      // Notify all waiting requests
      onTokenRefreshed(newToken);

      setRefreshing(false);

      // Retry the original request with new token
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axiosInstance(originalRequest);
    } catch (error) {
      console.error('%c[Axios]%c Token refresh failed:', 'color: #EF4444; font-weight: bold', 'color: inherit', error);
      await logout();
      removeTokenFromAxios();
      setRefreshing(false);
      redirectForSSOLogin();
      throw error;
    }
  },
);

export const setTokenToAxios = (token: string) => {
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
};

export const removeTokenFromAxios = () => {
  delete axiosInstance.defaults.headers.common.Authorization;
};

export const axiosServiceInstance = axiosInstance;
export default axiosInstance;
